import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import config from "../Config";
import { centsToDollarInputString, dollarsStringToCents } from "../Utils/money";
import Topbar from "../Components/Topbar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ImageSelectDialog from "../Components/ImageSelectDialog";

function ServiceEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showImageSelectDialog, setShowImageSelectDialog] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploadingImageIndex, setUploadingImageIndex] = useState(null);

  const [formData, setFormData] = useState({
    sub_category_id: "",
    title: "",
    amount: "",
    currency: "CAD",
    eta: "",
    subsequent_price: "",
    subtitle: "",
    tab_title: "",
    tab_subtitle: "",
    description: "",
    image: "",
    images: [],
    videos: [],
    label: "",
    available: true,
    allows_insurance: false,
    service_type: "HOUR",
    valid_extra_charge: ["CANCEL", "RESEDULE", "OTHER"],
    terms_and_conditions: [],
    faq: [],
    bottom_note: "",
    pricing_text: "",
    priority: 999,
  });

  const fetchService = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/service/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setService(data);
        setFormData({
          sub_category_id: data.sub_category_id,
          title: data.title,
          amount: centsToDollarInputString(data.amount),
          currency: data.currency,
          eta: data.eta,
          subsequent_price: centsToDollarInputString(data.subsequent_price),
          subtitle: data.subtitle || "",
          tab_title: data.tab_title || "",
          tab_subtitle: data.tab_subtitle || "",
          description: data.description || "",
          image: data.image || "",
          images: data.images || [],
          videos: data.videos || [],
          label: data.label || "",
          available: data.available,
          allows_insurance: data.allows_insurance || false,
          service_type: data.service_type || "HOUR",
          valid_extra_charge: data.valid_extra_charge || ["CANCEL", "RESEDULE", "OTHER"],
          terms_and_conditions: data.terms_and_conditions || [],
          faq: data.faq || [],
          bottom_note: data.bottom_note || "",
          pricing_text: data.pricing_text || "",
          priority: data.priority !== null && data.priority !== undefined ? data.priority : 999,
        });
        setImagePreview(data.image || "");
      } else {
        throw new Error("Failed to fetch service");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchSubCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/subcategories?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubCategories(data);
      } else {
        throw new Error("Failed to fetch subcategories");
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${config.apiUrl}/api/v1/admin/categories?disable_pagination=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      } else {
        throw new Error("Failed to fetch categories");
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, []);

  useEffect(() => {
    fetchService();
    fetchSubCategories();
    fetchCategories();
  }, [fetchService, fetchSubCategories, fetchCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    const token = localStorage.getItem("token");

    try {
      let imageUrl = formData.image;

      if (imageFile) {
        const uploadFormData = new FormData();
        uploadFormData.append("file", imageFile);

        const uploadResponse = await fetch(
          "https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload",
          {
            method: "POST",
            headers: {
              "X-Client": "ADMIN",
              Authorization: `Bearer ${token}`,
            },
            body: uploadFormData,
          }
        );

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          imageUrl = uploadData[0];
          toast.success("Image uploaded successfully!");
        } else {
          toast.error("Failed to upload image");
          throw new Error("Failed to upload image");
        }
      }

      const url = `${config.apiUrl}/api/v1/admin/service/${id}`;

      const amountCents = dollarsStringToCents(formData.amount);
      const subsequentCents = dollarsStringToCents(formData.subsequent_price);
      if (Number.isNaN(amountCents) || amountCents < 0) {
        toast.error('Enter a valid amount in dollars');
        throw new Error('Invalid amount');
      }
      if (Number.isNaN(subsequentCents) || subsequentCents < 0) {
        toast.error('Enter a valid subsequent price in dollars');
        throw new Error('Invalid subsequent price');
      }

      const updatedFormData = {
        ...formData,
        tab_title: formData.tab_title ? formData.tab_title.trim() || null : null,
        tab_subtitle: formData.tab_subtitle ? formData.tab_subtitle.trim() || null : null,
        image: imageUrl,
        amount: amountCents,
        subsequent_price: subsequentCents,
      };

      const defaultCharges = ['CANCEL', 'RESEDULE', 'OTHER'];
      const validExtraCharge = [...updatedFormData.valid_extra_charge];

      defaultCharges.forEach((charge) => {
        if (!validExtraCharge.includes(charge)) {
          validExtraCharge.push(charge);
        }
      });

      const priority =
        updatedFormData.priority !== null &&
        updatedFormData.priority !== undefined &&
        updatedFormData.priority !== ''
          ? updatedFormData.priority
          : 999;

      const dataToSend = {
        ...updatedFormData,
        valid_extra_charge: validExtraCharge,
        priority: priority,
      };

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        toast.success("Service updated successfully");
        navigate("/services");
      } else {
        throw new Error("Failed to update service");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleImageSelect = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageFile(file);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAdditionalImageUpload = async (file, index) => {
    try {
      const token = localStorage.getItem("token");
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);

      const uploadResponse = await fetch(
        "https://hspbreguarptwwdfhtuj.supabase.co/functions/v1/image-upload",
        {
          method: "POST",
          headers: {
            "X-Client": "ADMIN",
            Authorization: `Bearer ${token}`,
          },
          body: uploadFormData,
        }
      );

      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();
        const newImages = [...formData.images];
        newImages[index] = uploadData[0];
        setFormData({ ...formData, images: newImages });
        toast.success("Additional image uploaded successfully!");
      } else {
        toast.error("Failed to upload additional image");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Topbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Topbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-red-600">Service not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Topbar />
      <main className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Edit Service</h1>
            <button
              onClick={() => navigate("/services")}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Back to Services
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subcategory <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.sub_category_id}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sub_category_id: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              >
                <option value="">Select a subcategory</option>
                {subCategories.map((subCategory) => (
                  <option
                    key={subCategory.sub_category_id}
                    value={subCategory.sub_category_id}
                  >
                    {
                      categories.find(
                        (cat) => cat.category_id === subCategory.category_id
                      )?.title
                    }{" "}
                    - {subCategory.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Single door refrigerator check-up"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.service_type}
                onChange={(e) =>
                  setFormData({ ...formData, service_type: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              >
                <option value="HOUR">Hour</option>
                <option value="AUTO">Auto</option>
                <option value="QUOTATION">Quotation</option>
                <option value="QUANTITY">Quantity</option>
                <option value="DIAGNOSTIC">Diagnostic</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.service_type === "HOUR" ? "Amount for first hour (dollars)" : 
                   formData.service_type === "QUOTATION" ? "Price for quotation (dollars)" :
                   "Amount for first quantity (dollars)"} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      amount: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency <span className="text-red-500">*</span>
                </label>
                <select
                  disabled
                  value={formData.currency}
                  onChange={(e) =>
                    setFormData({ ...formData, currency: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select currency</option>
                  <option value="CAD">CAD</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.service_type === "QUANTITY" ? "Time for First Quantity (minutes)" : "ETA (minutes)"} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.eta}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      eta: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {["QUANTITY", "QUOTATION","DIAGNOSTIC","AUTO"].includes(formData.service_type)?"Price extra quantity (dollars)": "Subsequent price per hour (dollars)" } <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.subsequent_price}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      subsequent_price: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subtitle
              </label>
              <input
                type="text"
                value={formData.subtitle}
                onChange={(e) =>
                  setFormData({ ...formData, subtitle: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tab Title
              </label>
              <input
                type="text"
                value={formData.tab_title}
                onChange={(e) =>
                  setFormData({ ...formData, tab_title: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter tab title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tab Subtitle
              </label>
              <input
                type="text"
                value={formData.tab_subtitle}
                onChange={(e) =>
                  setFormData({ ...formData, tab_subtitle: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter tab subtitle"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Logo Image
              </label>
              {imagePreview && (
                <div className="mb-4">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-md"
                  />
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowImageSelectDialog(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {imagePreview ? "Edit Image" : "Upload Image"}
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Images
              </label>
              <div className="space-y-2">
                {formData.images.map((image, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    {image && (
                      <div className="flex-shrink-0">
                        <img
                          src={image}
                          alt={`Preview ${index + 1}`}
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <input
                      type="text"
                      value={image}
                      onChange={(e) => {
                        const newImages = [...formData.images];
                        newImages[index] = e.target.value;
                        setFormData({ ...formData, images: newImages });
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter image URL or upload image"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setUploadingImageIndex(index);
                        setShowImageSelectDialog(true);
                      }}
                      className="px-3 py-2 text-sm text-green-600 border border-green-600 rounded-md hover:bg-green-50"
                    >
                      Upload
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const newImages = formData.images.filter((_, i) => i !== index);
                        setFormData({ ...formData, images: newImages });
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      images: [...formData.images, ""]
                    });
                  }}
                  className="mt-2 px-4 py-2 text-sm text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50"
                >
                  Add Image
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Videos
              </label>
              <div className="space-y-2">
                {formData.videos.map((video, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={video}
                      onChange={(e) => {
                        const newVideos = [...formData.videos];
                        newVideos[index] = e.target.value;
                        setFormData({ ...formData, videos: newVideos });
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newVideos = formData.videos.filter((_, i) => i !== index);
                        setFormData({ ...formData, videos: newVideos });
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      videos: [...formData.videos, ""]
                    });
                  }}
                  className="mt-2 px-4 py-2 text-sm text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50"
                >
                  Add Video Link
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Label
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) =>
                  setFormData({ ...formData, label: e.target.value })
                }
                placeholder="e.g. NEW, QUICK etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <input
                type="number"
                value={formData.priority}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    priority: e.target.value ? parseInt(e.target.value) : 999,
                  })
                }
                placeholder="999"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="mt-1 text-sm text-gray-500">
                Lower number = higher priority. Default: 999
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valid Extra Charges
              </label>
              <div className="space-y-2">
                {[
                  "MATERIAL_COST",
                  "VISIT_COST",
                  "CANCEL_COST",
                  "EXTRA_WORKER_COST",
                  "RE_VISIT_COST",
                  "EXTRA_QUANTITY",
                  "EXTRA_TIME",
                  "CANCEL",
                  "RESEDULE",
                  "OTHER"
                ].map((charge) => (
                  <div key={charge} className="flex items-center">
                    <input
                      type="checkbox"
                      id={charge}
                      checked={["CANCEL", "RESEDULE", "OTHER"].includes(charge) ? true : formData.valid_extra_charge.includes(charge)}
                      onChange={(e) => {
                        if (["CANCEL", "RESEDULE", "OTHER"].includes(charge)) {
                          return; // Prevent changes for these three options
                        }
                        const newCharges = e.target.checked
                          ? [...formData.valid_extra_charge, charge]
                          : formData.valid_extra_charge.filter(c => c !== charge);
                        setFormData({ ...formData, valid_extra_charge: newCharges });
                      }}
                      disabled={["CANCEL", "RESEDULE", "OTHER"].includes(charge)}
                      className={`h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded ${
                        ["CANCEL", "RESEDULE", "OTHER"].includes(charge) ? "opacity-50" : ""
                      }`}
                    />
                    <label htmlFor={charge} className="ml-2 block text-sm text-gray-900">
                      {charge.replace(/_/g, ' ')}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Terms and Conditions
              </label>
              <div className="space-y-2">
                {formData.terms_and_conditions.map((term, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={term}
                      onChange={(e) => {
                        const newTerms = [...formData.terms_and_conditions];
                        newTerms[index] = e.target.value;
                        setFormData({ ...formData, terms_and_conditions: newTerms });
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter term and condition"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newTerms = formData.terms_and_conditions.filter((_, i) => i !== index);
                        setFormData({ ...formData, terms_and_conditions: newTerms });
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      terms_and_conditions: [...formData.terms_and_conditions, ""]
                    });
                  }}
                  className="mt-2 px-4 py-2 text-sm text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50"
                >
                  Add Term
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                FAQ
              </label>
              <div className="space-y-4">
                {formData.faq.map((faqItem, index) => (
                  <div key={index} className="border border-gray-200 rounded-md p-4">
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={faqItem.question || ""}
                        onChange={(e) => {
                          const newFaq = [...formData.faq];
                          newFaq[index] = { ...newFaq[index], question: e.target.value };
                          setFormData({ ...formData, faq: newFaq });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter question"
                      />
                      <textarea
                        value={faqItem.answer || ""}
                        onChange={(e) => {
                          const newFaq = [...formData.faq];
                          newFaq[index] = { ...newFaq[index], answer: e.target.value };
                          setFormData({ ...formData, faq: newFaq });
                        }}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter answer"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newFaq = formData.faq.filter((_, i) => i !== index);
                        setFormData({ ...formData, faq: newFaq });
                      }}
                      className="mt-2 text-red-600 hover:text-red-900 text-sm"
                    >
                      Remove FAQ
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      faq: [...formData.faq, { question: "", answer: "" }]
                    });
                  }}
                  className="mt-2 px-4 py-2 text-sm text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50"
                >
                  Add FAQ
                </button>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.available}
                onChange={(e) =>
                  setFormData({ ...formData, available: e.target.checked })
                }
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Available
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.allows_insurance}
                onChange={(e) =>
                  setFormData({ ...formData, allows_insurance: e.target.checked })
                }
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Allows Insurance
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bottom Note
              </label>
              <textarea
                value={formData.bottom_note}
                onChange={(e) =>
                  setFormData({ ...formData, bottom_note: e.target.value })
                }
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter bottom note (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pricing Text
              </label>
              <textarea
                value={formData.pricing_text}
                onChange={(e) =>
                  setFormData({ ...formData, pricing_text: e.target.value })
                }
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter pricing text (optional)"
              />
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button
                type="button"
                onClick={() => navigate("/services")}
                disabled={updating}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updating}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {updating ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  "Update Service"
                )}
              </button>
            </div>
          </form>
        </div>
      </main>

      <ImageSelectDialog
        open={showImageSelectDialog}
        onClose={() => {
          setShowImageSelectDialog(false);
          setUploadingImageIndex(null);
        }}
        onFileProcessed={(file) => {
          if (uploadingImageIndex !== null) {
            // Handle additional image upload
            handleAdditionalImageUpload(file, uploadingImageIndex);
            setUploadingImageIndex(null);
          } else {
            // Handle main logo image
            handleImageSelect(file);
          }
          setShowImageSelectDialog(false);
        }}
      />
      
      <ToastContainer />

      {/* Full-screen loader overlay */}
      {updating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120]">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center space-y-4">
            <svg className="animate-spin h-12 w-12 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <div className="text-lg font-medium text-gray-900">Updating Service...</div>
            <div className="text-sm text-gray-600">Please don't close this page</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServiceEdit;